<template>
  <div class="order-history-container">
    <div class="header">
      <h2>Order History</h2>

      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="tab in ['Table View', 'Domestic Order', 'Container Order', 'Customer Backorder', 'All']"
          :key="tab"
          @click="changeTab(tab)"
          :class="['tab-btn', { active: activeTab === tab }]"
        >
          {{ tab }}
        </button>
      </div>

      <!-- Filters -->
      <div
        v-if="isMobile"
        class="d-flex justify-content-between align-items-center mb-2 px-1"
      >
        <b-button
          size="sm"
          variant="outline-primary"
          @click="filtersVisible = !filtersVisible"
        >
          <b-icon-funnel-fill class="mr-1"></b-icon-funnel-fill>
          {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
        </b-button>
      </div>

      <b-collapse :visible="!isMobile || filtersVisible" class="mb-2">
        <div class="filters">
          <select v-model="filters.orderType">
            <option value="">All Order Types</option>
            <option value="Domestic Order">Domestic Orders</option>
            <option value="Container Order">Container Orders</option>
            <option value="Customer Backorder">Customer Backorders</option>
          </select>

          <!-- Status Filter -->
          <select v-model="filters.status">
            <option value="">All Statuses</option>
            <option v-for="status in availableStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>

          <input
            v-model="filters.orderId"
            type="number"
            placeholder="Order ID"
          >
          <input
            v-model="filters.productSku"
            placeholder="Product SKU"
          >

          <!-- Filter by change type -->
          <select v-model="filters.changeType">
            <option value="">All Changes</option>
            <option value="added">Added Items</option>
            <option value="updated">Updated Items</option>
            <option value="deleted">Deleted Items</option>
          </select>

          <!-- Date Range Filter -->
          <div class="date-filter">
            <input
              v-model="filters.dateFrom"
              type="date"
              placeholder="From Date"
            >
            <input
              v-model="filters.dateTo"
              type="date"
              placeholder="To Date"
            >
          </div>

          <button @click="clearFilters" class="clear-btn">Clear Filters</button>

          <!-- Bulk actions -->
          <div class="bulk-actions" v-if="selectedItems.length > 0">
            <span class="selected-count">{{ selectedItems.length }} selected</span>
            <button @click="exportSelected" class="export-btn">Export Selected</button>
            <button @click="clearSelection" class="clear-selection-btn">Clear</button>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- Content -->
    <div class="history-content">
      <div v-if="loading" class="loading">Loading history...</div>
      <div v-else-if="error" class="error">
        Error loading history: {{ error }}
        <button @click="loadHistory" class="retry-btn">Retry</button>
      </div>
      <div v-else-if="filteredHistory.length === 0" class="no-data">
        No history records found
      </div>

      <!-- Results Count -->
      <div v-if="!loading && history.length > 0" class="results-info">
        Showing {{ filteredHistory.length }} of {{ history.length }} records
      </div>

      <!-- ✅ Basic Table View -->
      <div v-if="activeTab === 'Table View' || activeTab === 'Changed Items'" class="table-wrapper">
        <table class="basic-table">
          <thead>
            <tr>
              <th v-if="activeTab === 'Changed Items'">
                <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
              </th>
              <th>Order ID</th>
              <th>Type</th>
              <th>SKU/PO#</th>
              <th>Status</th>
              <th>Changed By</th>
              <th>Date</th>
              <th>Reason</th>
              <th>Changes</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginatedHistory"
              :key="item.id"
              :class="{'selected': selectedItems.includes(item.id)}"
            >
              <td v-if="activeTab === 'Changed Items'">
                <input type="checkbox" v-model="selectedItems" :value="item.id">
              </td>
              <td>{{ item.order_id }}</td>
              <td>{{ getShortOrderType(item.order_type) }}</td>
              <td>{{ item.product_sku }}</td>
              <td>{{ getCurrentStatus(item) }}</td>
              <td>{{ item.changed_by || 'System' }}</td>
              <td>{{ formatShortDate(item.change_date) }}</td>
              <td>{{ truncateText(item.change_reason, 20) }}</td>
              <td>
                <div class="basic-changes">
                  <span v-for="(change, index) in getFormattedChanges(item)" :key="change.field">
                    {{ change.field }}
                    <span v-if="index < getFormattedChanges(item).length - 1">, </span>
                  </span>
                </div>
              </td>
              <td>
                <button
                  @click="viewItemDetails(item)"
                  class="basic-view-btn"
                  title="View details"
                >
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Default card/grid layout -->
      <div v-else class="history-list">
        <div
          class="history-item"
          v-for="item in paginatedHistory"
          :key="item.id"
          :class="{'changed-item': hasSignificantChanges(item)}"
        >
          <div class="item-header">
            <div class="header-top">
              <span class="order-type">
                {{ item.order_type }}
              </span>
              <span class="order-id">Order #{{ item.order_id }}</span>
              <span class="status-text">
                {{ getCurrentStatus(item) }}
              </span>
              <span v-if="hasSignificantChanges(item)" class="change-indicator" title="Significant changes">
                ⚡
              </span>
            </div>
            <div class="meta">
              <div style="font-weight: bold; font-size: 15px;" class="sku">SKU: {{ item.product_sku }}</div>
              <div class="date">{{ formatDate(item.change_date) }}</div>
              <div class="user">By: {{ item.changed_by || 'System' }}</div>
            </div>
          </div>
          <div class="change-reason">{{ item.change_reason }}</div>
          <div class="changes">
            <div
              v-for="change in getFormattedChanges(item)"
              :key="change.field"
              class="change-item"
            >
              <span class="field-name">{{ change.field }}:</span>
              <span class="old-value" v-if="change.old !== null && change.old !== undefined">
                {{ change.old }}
              </span>
              <span class="arrow">→</span>
              <span class="new-value" v-if="change.new !== null && change.new !== undefined">
                {{ change.new }}
              </span>
            </div>
          </div>
          <div class="item-actions">
            <button @click="viewItemDetails(item)" class="action-btn">View Details</button>
          </div>
        </div>
      </div>

      <!-- Frontend Pagination -->
      <div v-if="filteredHistory.length > 0" class="pagination">
        <button
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          Previous
        </button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          Next
        </button>
        <span class="total-items">(Total: {{ filteredHistory.length }} items)</span>

        <!-- Page Size Selector -->
        <select v-model="pageSize" @change="currentPage = 1" class="page-size-select">
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
        </select>
      </div>
    </div>

    <!-- Item Details Modal -->
    <div v-if="selectedItem" class="modal-overlay" @click.self="selectedItem = null">
      <div class="modal-content">
        <h3>Change Details</h3>
        <div class="modal-body">
          <div class="detail-row">
            <span class="detail-label">Order ID:</span>
            <span class="detail-value">{{ selectedItem.order_id }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">SKU:</span>
            <span class="detail-value">{{ selectedItem.product_sku }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Current Status:</span>
            <span class="detail-value">
              {{ getCurrentStatus(selectedItem) }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Changed By:</span>
            <span class="detail-value">{{ selectedItem.changed_by || 'System' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Date:</span>
            <span class="detail-value">{{ formatDate(selectedItem.change_date) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Reason:</span>
            <span class="detail-value">{{ selectedItem.change_reason }}</span>
          </div>
          <div class="changes-details">
            <h4>Changes Made:</h4>
            <div v-for="change in getFormattedChanges(selectedItem)" :key="change.field" class="change-detail">
              <strong>{{ change.field }}:</strong>
              <span class="old">{{ change.old || 'N/A' }}</span> →
              <span class="new">{{ change.new || 'N/A' }}</span>
            </div>
          </div>
        </div>
        <button @click="selectedItem = null" class="close-btn">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import Requestor from '@/utilities/Requestor';
import { BIconFunnelFill } from 'bootstrap-vue'

export default {
  name: 'OrderHistory',
  data() {
    return {
      history: [], // All loaded data
      loading: false,
      error: null,
      activeTab: 'Table View',
      selectedItem: null,
      selectedItems: [],
      selectAll: false,
      currentPage: 1,
      pageSize: 20,
      filters: {
        orderType: '',
        status: '',
        orderId: '',
        productSku: '',
        changeType: '',
        dateFrom: '',
        dateTo: ''
      },
      statusLabels: {
        'Domestic Order': {
          1: 'NEW',
          2: 'ORDERED',
          3: 'SCHEDULED',
          4: 'TRANSIT',
          5: 'PARTIAL',
          6: 'RECEIVED',
          7: 'COMPLETED',
          8: 'ARCHIVED'
        },
        'Container Order': {
          1: 'NEW',
          2: 'ORDERED',
          3: 'SCHEDULED',
          4: 'PARTIAL',
          5: 'RECEIVED',
          6: 'COMPLETED',
          7: 'ARCHIVED'
        },
        'Customer Backorder': {
          1: 'NEW',
          2: 'ORDERED',
          3: 'SCHEDULED',
          4: 'TRANSIT',
          5: 'PARTIAL',
          6: 'RECEIVED',
          7: 'COMPLETED',
          8: 'ARCHIVED'
        }
      },
      filtersVisible: false,
      isMobile: false,
      significantFields: ['ORDER QTY', 'SCH QTY', 'RCV QTY', 'STATUS', 'ACTION']
    };
  },
  mounted() {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  created() {
    this.debouncedLoadHistory = debounce(this.loadHistory, 500);
    this.loadHistory();
  },
  computed: {
    // Main filtering logic
    filteredHistory() {
      let filtered = this.history;

      // Apply tab filter
      if (this.activeTab !== 'All' && this.activeTab !== 'Table View' && this.activeTab !== 'Changed Items') {
        filtered = filtered.filter(h => h.order_type === this.activeTab);
      }

      // Apply order type filter
      if (this.filters.orderType) {
        filtered = filtered.filter(item => item.order_type === this.filters.orderType);
      }

      // Apply status filter
      if (this.filters.status) {
        filtered = filtered.filter(item => {
          const currentStatus = this.getCurrentStatus(item);
          return currentStatus === this.filters.status;
        });
      }

      // Apply order ID filter
      if (this.filters.orderId) {
        filtered = filtered.filter(item =>
          item.order_id.toString().includes(this.filters.orderId.toString())
        );
      }

      // Apply SKU filter
      if (this.filters.productSku) {
        const skuLower = this.filters.productSku.toLowerCase();
        filtered = filtered.filter(item =>
          item.product_sku.toLowerCase().includes(skuLower)
        );
      }

      // Apply date range filter
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom);
        filtered = filtered.filter(item => new Date(item.change_date) >= fromDate);
      }

      if (this.filters.dateTo) {
        const toDate = new Date(this.filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of the day
        filtered = filtered.filter(item => new Date(item.change_date) <= toDate);
      }

      // Apply change type filter
      if (this.filters.changeType) {
        filtered = filtered.filter(item => {
          const changes = this.getFormattedChanges(item);
          if (this.filters.changeType === 'added') {
            return changes.some(c => c.field === 'ACTION' && c.new === 'Added');
          } else if (this.filters.changeType === 'updated') {
            return changes.some(c => c.field !== 'ACTION' && (c.old !== null || c.new !== null));
          } else if (this.filters.changeType === 'deleted') {
            return changes.some(c => c.field === 'ACTION' && c.new === 'Deleted');
          }
          return true;
        });
      }

      return filtered;
    },

    // Paginated results
    paginatedHistory() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredHistory.slice(startIndex, endIndex);
    },

    // Total pages for pagination
    totalPages() {
      return Math.ceil(this.filteredHistory.length / this.pageSize);
    },

    // Available statuses for filter dropdown
    availableStatuses() {
      const allStatuses = new Set();

      this.history.forEach(item => {
        const currentStatus = this.getCurrentStatus(item);
        if (currentStatus && currentStatus !== 'N/A') {
          allStatuses.add(currentStatus);
        }
      });

      return Array.from(allStatuses).map(status => ({
        value: status,
        label: status
      })).sort((a, b) => a.label.localeCompare(b.label));
    }
  },
  watch: {
    // Reset to first page when filters change
    filters: {
      handler() {
        this.currentPage = 1;
      },
      deep: true
    },

    // Reset to first page when tab changes
    activeTab() {
      this.currentPage = 1;
    }
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768
    },
    // Load all data once (no filters in API call)
    loadHistory() {
      this.loading = true;
      this.error = null;

      let that = this;
      let successCallback = function(response) {
        if (response.data.error) {
          that.error = response.data.error;
          that.history = [];
        } else {
          that.history = response.data.result || [];
        }
        that.loading = false;
      };

      let errorCallback = function(error) {
        console.error("Error loading order history:", error);
        that.error = "Failed to load history";
        that.loading = false;
      };

      // No filters in API call - get all data
      Requestor.getSyncP("/api/vendor/get_order_history/", { paginate: false }, this, successCallback, errorCallback);
    },

    // Get current status for an item
    getCurrentStatus(item) {
      if (item.status_new !== null && item.status_new !== undefined) {
        const orderTypeLabels = this.statusLabels[item.order_type] || {};
        return orderTypeLabels[item.status_new] || item.status_new || 'N/A';
      } else if (item.status_prev !== null && item.status_prev !== undefined) {
        const orderTypeLabels = this.statusLabels[item.order_type] || {};
        return orderTypeLabels[item.status_prev] || item.status_prev || 'N/A';
      }
      return 'N/A';
    },

    changeTab(tab) {
      this.activeTab = tab;
    },

    getFormattedChanges(item) {
      const changes = [];
      const fieldPairs = [
        { field: 'ORDER QTY', prev: 'order_qty_prev', new: 'order_qty_new' },
        { field: 'SCH QTY', prev: 'sch_qty_prev', new: 'sch_qty_new' },
        { field: 'RCV QTY', prev: 'rcv_qty_prev', new: 'rcv_qty_new' },
        { field: 'STATUS', prev: 'status_prev', new: 'status_new' },
        { field: 'SCH DATE', prev: 'sch_date_prev', new: 'sch_date_new' },
        { field: 'RECEIVE DATE', prev: 'receive_date_prev', new: 'receive_date_new' },
        { field: 'COMMENTS', prev: 'comments_prev', new: 'comments_new' },
        { field: 'ACTION', prev: 'action_prev', new: 'action_new' }
      ];

      fieldPairs.forEach(pair => {
        const oldVal = item[pair.prev];
        const newVal = item[pair.new];
        if (oldVal !== null || newVal !== null) {
          let formattedOld = oldVal;
          let formattedNew = newVal;

          if (pair.field === 'STATUS') {
            const orderTypeLabels = this.statusLabels[item.order_type] || {};
            formattedOld = orderTypeLabels[oldVal] || oldVal;
            formattedNew = orderTypeLabels[newVal] || newVal;
          }

          changes.push({
            field: pair.field,
            old: formattedOld,
            new: formattedNew
          });
        }
      });
      return changes;
    },

    hasSignificantChanges(item) {
      const changes = this.getFormattedChanges(item);
      return changes.some(change =>
        this.significantFields.includes(change.field) &&
        (change.old !== change.new)
      );
    },

    isSignificantChange(change) {
      return this.significantFields.includes(change.field);
    },

    viewItemDetails(item) {
      this.selectedItem = item;
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.paginatedHistory.map(item => item.id);
      } else {
        this.selectedItems = [];
      }
    },

    exportSelected() {
      const selectedData = this.history.filter(item =>
        this.selectedItems.includes(item.id)
      );
      console.log('Exporting selected items:', selectedData);
      // Implement your export logic here
      alert(`Exporting ${selectedData.length} items`);
    },

    clearSelection() {
      this.selectedItems = [];
      this.selectAll = false;
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString();
    },

    formatShortDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    getShortOrderType(orderType) {
      const types = {
        'Domestic Order': 'Domestic',
        'Container Order': 'Container',
        'Customer Backorder': 'Backorder'
      };
      return types[orderType] || orderType;
    },

    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    clearFilters() {
      this.filters = {
        orderType: '',
        status: '',
        orderId: '',
        productSku: '',
        changeType: '',
        dateFrom: '',
        dateTo: ''
      };
      this.selectedItems = [];
      this.selectAll = false;
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped>
.results-info {
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #333;
}

/* Date filter */
.date-filter {
  display: flex;
  gap: 8px;
}

.date-filter input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
}

/* Page size selector */
.page-size-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

/* Basic table styles */
.basic-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: white;
  border: 1px solid #ddd;
}

.basic-table th {
  background: #f9f9f9;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #ddd;
  border-right: 1px solid #ddd;
}

.basic-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #ddd;
  border-right: 1px solid #ddd;
}

.basic-table tr:hover {
  background-color: #f5f5f5;
}

.basic-table .selected {
  background-color: #e6f3ff;
}

/* Basic changes display */
.basic-changes {
  font-size: 12px;
  color: #666;
}

/* Basic view button */
.basic-view-btn {
  background: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.basic-view-btn:hover {
  background: #e0e0e0;
}

/* Keep existing styles for other components */
.order-history-container {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 20px;
}

h2 {
  margin-bottom: 15px;
  color: #333;
}

.tabs {
  display: flex;
  gap: 12px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 16px;
}

.tab-btn {
  position: relative;
  background: none;
  border: none;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: color 0.3s ease;
}

.tab-btn:hover {
  color: #1976d2;
}

.tab-btn.active {
  color: #1976d2;
  font-weight: 600;
}

.tab-btn.active::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 3px;
  background: #1976d2;
  border-radius: 2px;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  from { width: 0; }
  to { width: 100%; }
}

.filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 20px;
}

.filters select,
.filters input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  transition: border 0.3s ease, box-shadow 0.3s ease;
}

.filters select:focus,
.filters input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.clear-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.clear-btn:hover {
  background: #5a6268;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.selected-count {
  font-weight: bold;
  color: #495057;
}

.export-btn, .clear-selection-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  background: white;
}

.export-btn {
  color: #28a745;
  border-color: #28a745;
}

.clear-selection-btn {
  color: #dc3545;
  border-color: #dc3545;
}

.loading, .error, .no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

.error {
  color: #dc3545;
}

.retry-btn {
  margin-left: 10px;
  padding: 6px 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.history-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.history-item {
  flex: 1 1 calc(33.333% - 20px);
  max-width: 320px;
  min-width: 260px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.item-header {
  margin-bottom: 12px;
}

.header-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.order-type {
  font-weight: 600;
  font-size: 14px;
}

.order-id {
  font-weight: 600;
  color: #555;
}

.status-text {
  font-size: 12px;
  color: #666;
}

.change-indicator {
  color: #ff6b35;
  font-size: 16px;
  margin-left: auto;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: #666;
}

.sku {
  font-weight: 600;
  color: #333;
}

.change-reason {
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 13px;
}

.changes {
  margin-bottom: 12px;
}

.change-item {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 6px 10px;
  margin: 4px 0;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-name {
  font-weight: 600;
}

.old-value {
  color: #666;
  text-decoration: line-through;
  margin: 0 4px;
}

.arrow {
  color: #666;
  margin: 0 4px;
}

.new-value {
  color: #333;
  font-weight: 600;
  margin: 0 4px;
}

.item-actions {
  margin-top: 12px;
  text-align: right;
}

.action-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.action-btn:hover {
  background: #5a6268;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #6c757d;
  color: white;
  border-color: #6c757d;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: #333;
}

.total-items {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 4px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid #ddd;
}

.modal-body {
  margin: 20px 0;
}

.detail-row {
  display: flex;
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.detail-label {
  font-weight: bold;
  min-width: 120px;
  color: #333;
}

.detail-value {
  flex: 1;
  color: #666;
}

.changes-details {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.change-detail {
  margin: 8px 0;
  padding: 8px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #6c757d;
}

.change-detail .old {
  color: #666;
  text-decoration: line-through;
}

.change-detail .new {
  color: #333;
  font-weight: bold;
}

.close-btn {
  background: #6c757d;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .tabs {
    overflow-x: auto;
    border-bottom: none;
  }

  .tabs::-webkit-scrollbar {
    display: none;
  }

  .tab-btn {
    flex: 1;
    min-width: 150px;
    margin-bottom: 5px;
  }

  .tab-btn:first-child {
    flex: 1;
    min-width: 120px;
    margin-bottom: 5px;
  }

  .tab-btn:nth-last-child(2) {
    flex: 1;
    min-width: 170px;
    margin-bottom: 5px;
  }

  .tab-btn:last-child {
    flex: 1;
    min-width: 70px;
    margin-bottom: 5px;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .bulk-actions {
    flex-wrap: wrap;
  }

  .modal-content {
    margin: 20px;
    width: calc(100% - 40px);
  }

  .pagination {
    flex-direction: column;
    gap: 10px;
  }

  .table-wrapper {
    height: auto;
    overflow-x: auto;
    overflow-y: auto;
  }

  .basic-table {
    font-size: 12px;
  }

  .basic-table th:first-child,
  .basic-table td:first-child {
    min-width: 60px;
  }

  .basic-table th:nth-last-child(2),
  .basic-table td:nth-last-child(2) {
    min-width: 250px;
  }

  .basic-table th:nth-last-child(3),
  .basic-table td:nth-last-child(3) {
    min-width: 150px;
  }

  .basic-table th,
  .basic-table td {
    min-width: 100px;
    padding: 8px 6px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/VendorOrders/VendorOrdersLogs.vue